import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { useCache } from '../hooks/useCache';

const StatsCards = () => {
  const fetcher = useMemo(() => () => api.getAdminHomePage(), []);
  const { data: cachedData, loading: cacheLoading, refetch } = useCache(
    'admin_homepage_stats',
    fetcher,
    { ttl: 2 * 60 * 1000 } // 2 minutes cache
  );

  const [stats, setStats] = useState([
    {
      id: 4,
      label: 'عدد المستخدمين',
      value: '0',
      change: '0%',
      color: '#E96A3A',
      bgGradient: 'linear-gradient(135deg, rgba(233, 106, 58, 0.1) 0%, rgba(233, 106, 58, 0.05) 100%)',
      borderColor: 'rgba(233, 106, 58, 0.22)'
    },
    {
      id: 3,
      label: 'عدد المطابخ',
      value: '0',
      change: '0%',
      color: '#3FB984',
      bgGradient: 'linear-gradient(135deg, rgba(63, 185, 132, 0.1) 0%, rgba(63, 185, 132, 0.05) 100%)',
      borderColor: 'rgba(63, 185, 132, 0.15)'
    },
    {
      id: 2,
      label: 'عدد المتاجر',
      value: '0',
      change: '0%',
      color: '#F27D72',
      bgGradient: 'linear-gradient(135deg, rgba(242, 125, 114, 0.1) 0%, rgba(242, 125, 114, 0.05) 100%)',
      borderColor: 'rgba(242, 125, 114, 0.15)'
    },
    {
      id: 1,
      label: 'عدد الطلبات',
      value: '0',
      change: '0%',
      color: '#D8B4A0',
      bgGradient: 'linear-gradient(135deg, rgba(216, 180, 160, 0.1) 0%, rgba(216, 180, 160, 0.05) 100%)',
      borderColor: 'rgba(216, 180, 160, 0.15)'
    }
  ]);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
      try {
        setLoading(true);
        const data = cachedData || await api.getAdminHomePage();
        
        if (data && data.counts) {
          setStats([
            {
              id: 4,
              label: 'عدد المستخدمين',
              value: (data.counts.users?.total || 0).toLocaleString('ar-EG'),
              change: `${(data.counts.users?.growthPctLastMonth || 0) >= 0 ? '+' : ''}${data.counts.users?.growthPctLastMonth || 0}%`,
              color: '#E96A3A',
              bgGradient: 'linear-gradient(135deg, rgba(233, 106, 58, 0.1) 0%, rgba(233, 106, 58, 0.05) 100%)',
              borderColor: 'rgba(233, 106, 58, 0.22)'
            },
            {
              id: 3,
              label: 'عدد المطابخ',
              value: (data.counts.restaurants?.total || 0).toLocaleString('ar-EG'),
              change: `${(data.counts.restaurants?.growthPctLastMonth || 0) >= 0 ? '+' : ''}${data.counts.restaurants?.growthPctLastMonth || 0}%`,
              color: '#3FB984',
              bgGradient: 'linear-gradient(135deg, rgba(63, 185, 132, 0.1) 0%, rgba(63, 185, 132, 0.05) 100%)',
              borderColor: 'rgba(63, 185, 132, 0.15)'
            },
            {
              id: 2,
              label: 'عدد المتاجر',
              value: (data.counts.stores?.total || 0).toLocaleString('ar-EG'),
              change: `${(data.counts.stores?.growthPctLastMonth || 0) >= 0 ? '+' : ''}${data.counts.stores?.growthPctLastMonth || 0}%`,
              color: '#F27D72',
              bgGradient: 'linear-gradient(135deg, rgba(242, 125, 114, 0.1) 0%, rgba(242, 125, 114, 0.05) 100%)',
              borderColor: 'rgba(242, 125, 114, 0.15)'
            },
            {
              id: 1,
              label: 'عدد الطلبات',
              value: (data.counts.orders?.total || 0).toLocaleString('ar-EG'),
              change: `${(data.counts.orders?.growthPctLastMonth || 0) >= 0 ? '+' : ''}${data.counts.orders?.growthPctLastMonth || 0}%`,
              color: '#D8B4A0',
              bgGradient: 'linear-gradient(135deg, rgba(216, 180, 160, 0.1) 0%, rgba(216, 180, 160, 0.05) 100%)',
              borderColor: 'rgba(216, 180, 160, 0.15)'
            }
          ]);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
        console.error('Error details:', error.message, error.stack);
        // Show user-friendly error message
        if (error.message && error.message.includes('لا يمكن الاتصال')) {
          console.warn('⚠️ الباك اند غير متاح. تأكد من تشغيله على http://localhost:3000');
        }
      } finally {
        setLoading(false);
      }
    }, [cachedData]);

  useEffect(() => {
    if (!cacheLoading) {
      fetchStats();
    }
  }, [cachedData, cacheLoading, fetchStats]);
  
  useEffect(() => {
    // Refresh every 2 minutes (cache TTL)
    const interval = setInterval(() => {
      refetch();
    }, 2 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [refetch]);

  if (loading) {
    return (
      <>
        {stats.map((stat) => (
          <div
            key={stat.id}
            style={{ 
              width: '100%',
              minHeight: '160px',
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              padding: '28px',
              border: `1px solid ${stat.borderColor}`,
              boxShadow: '0px 4px 23px rgba(0, 0, 0, 0.04)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              boxSizing: 'border-box',
            }}
          >
            <div style={{ color: '#666A7A', fontFamily: 'Cairo, sans-serif' }}>جاري التحميل...</div>
          </div>
        ))}
      </>
    );
  }

  return (
    <>
      {stats.map((stat) => (
        <div
          key={stat.id}
          style={{ 
            width: '100%',
            minHeight: '160px',
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            padding: '28px',
            border: `1px solid ${stat.borderColor}`,
            boxShadow: '0px 4px 23px rgba(0, 0, 0, 0.04)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            boxSizing: 'border-box',
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = `0px 8px 24px ${stat.color}20`;
            e.currentTarget.style.borderColor = stat.color;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0px 4px 23px rgba(0, 0, 0, 0.04)';
            e.currentTarget.style.borderColor = stat.borderColor;
          }}
        >
          {/* Background Gradient */}
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '100%',
            height: '100%',
            background: stat.bgGradient,
            opacity: 0.6,
            zIndex: 0
          }}></div>

          <div style={{ position: 'relative', zIndex: 1, width: '100%' }}>
            {/* Header Section */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'row',
              alignItems: 'flex-start', 
              justifyContent: 'space-between',
              marginBottom: '20px',
              width: '100%'
            }}>
              {/* Label */}
              <p style={{ 
                color: '#666A7A',
                fontSize: '14px',
                fontFamily: 'Cairo, sans-serif',
                fontWeight: 500,
                margin: 0,
                textAlign: 'right'
              }}>
                {stat.label}
              </p>

              {/* Change Badge */}
              <div style={{
                backgroundColor: `${stat.color}15`,
                borderRadius: '8px',
                padding: '4px 10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <span style={{ 
                  color: stat.color,
                  fontSize: '12px',
                  fontFamily: 'Cairo, sans-serif',
                  fontWeight: 700,
                  lineHeight: '1'
                }}>
                  {stat.change}
                </span>
              </div>
            </div>

            {/* Value Section */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'row',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              gap: '16px',
              width: '100%'
            }}>
              {/* Main Value */}
              <h3 style={{ 
                color: '#2B2F3F',
                fontSize: '32px',
                fontFamily: 'El Messiri, sans-serif',
                fontWeight: 700,
                lineHeight: '1.2',
                margin: 0,
                textAlign: 'right'
              }}>
                {stat.value}
              </h3>

              {/* Mini Chart */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'flex-end',
                justifyContent: 'center',
                gap: '3px',
                height: '40px',
                flexShrink: 0
              }}>
                {[65, 75, 60, 80, 70, 85, 90].map((height, index) => (
                  <div
                    key={index}
                    style={{
                      width: '4px',
                      height: `${height}%`,
                      backgroundColor: stat.color,
                      borderRadius: '2px',
                      opacity: 0.7
                    }}
                  ></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

export default StatsCards;
